import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
// import { useDispatch } from 'react-redux' // Removed for lint cleanup
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
  ListItem,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  Chip,
} from '@mui/material'
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Storefront as StorefrontIcon,
  OpenInNew as OpenInNewIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Instagram as InstagramIcon,
  ShoppingBag as ShoppingBagIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
} from '@mui/icons-material'// import { PermissionService } from '../services/permissionService' // Removed for lint cleanup
import { getNavigationItems } from '../config/navigationConfig'
import { useAuthStore } from '../stores/authStore'
import Logo from './ui/Logo'
import QuickActions from './ui/QuickActions'
import UserProfileDropdown from './ui/UserProfileDropdown'
import Breadcrumbs from './ui/Breadcrumbs'
import { SIMULATED_ROLES, SimulatedRoleKey } from '../utils/simulatedRoles'
import { QuickActionType, MenuItem } from '../types/navigation'

const drawerWidth = 280
const drawerWidthCollapsed = 72

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  console.log('Layout component rendering...')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'))

  const [mobileOpen, setMobileOpen] = useState(false)
  const [sidebarOpen] = useState(!isMobile)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [expandedItems, setExpandedItems] = useState<string[]>(['Orders', 'Products', 'Inventory'])
  const [currentTime, setCurrentTime] = useState(new Date())
  const { user, setUser, hasPermission, hasAnyPermission, logout: zustandLogout } = useAuthStore()
  const [actualUser] = useState(user) // Save actual user for reset

  const navigate = useNavigate()
  const location = useLocation()

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [])

  // Auto-collapse sidebar on tablet for better space utilization
  useEffect(() => {
    if (isTablet) {
      setSidebarCollapsed(true)
    } else if (!isMobile) {
      setSidebarCollapsed(false)
    }
  }, [isTablet, isMobile])

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen)
    } else {
      setSidebarCollapsed(!sidebarCollapsed)
    }
  }

  const handleSidebarClose = () => {
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const handleLogout = () => {
    zustandLogout()
    navigate('/login')
  }

  const handleQuickAction = (actionType: QuickActionType) => {
    console.log('Quick action triggered:', actionType)
  }

  const handleExpandClick = useCallback((itemText: string) => {
    setExpandedItems(prev =>
      prev.includes(itemText)
        ? prev.filter(item => item !== itemText)
        : [...prev, itemText]
    )
  }, [])

  // Memoize menu items to prevent recreation on every render
  const menuItems = useMemo(() => {
    const items = getNavigationItems()
    return items
  }, [])

  // Memoize filtered menu items based on exact permissions from DB
  const visibleMenuItems = useMemo(() => {
    if (!user) return []

    // Improved super admin check for the filter
    const isSuperAdmin = user.is_superuser ||
      user.role === 'super_admin' ||
      user.role === 'Super Administrator'

    if (isSuperAdmin) return menuItems

    const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
      return items.filter(item => {
        // Always show separators, section labels, and external links (no permission check)
        if (item.separator || item.external || item.sectionLabel) return true
        if (!item.permission && !item.requiredPermissions) return true

        const itemHasPermission = item.permission ? hasPermission(item.permission) : true
        const itemHasRequired = item.requiredPermissions ? hasAnyPermission(item.requiredPermissions) : true

        // Check if any children have permissions (for parent items)
        const hasChildrenWithPermissions = item.children
          ? item.children.some(child => {
            if (!child.permission && !child.requiredPermissions) return true
            const childHasPerm = child.permission ? hasPermission(child.permission) : true
            const childHasReq = child.requiredPermissions ? hasAnyPermission(child.requiredPermissions) : true
            return childHasPerm && childHasReq
          })
          : false

        return (itemHasPermission && itemHasRequired) || hasChildrenWithPermissions
      })
    }

    return filterMenuItems(menuItems)
  }, [menuItems, user])

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    // Handle section label
    if (item.sectionLabel) {
      if (sidebarCollapsed) return null  // hide when collapsed
      return (
        <Box
          key={item.text}
          sx={{
            px: 2,
            pt: 1.5,
            pb: 0.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              userSelect: 'none',
            }}
          >
            {item.text}
          </Typography>
        </Box>
      )
    }

    // Handle separator
    if (item.separator) {
      return (
        <Box
          key={item.text}
          sx={{
            height: 1,
            bgcolor: 'divider',
            my: 2,
            mx: sidebarCollapsed ? 1 : 2,
          }}
        />
      )
    }

    const isExpanded = expandedItems.includes(item.text)
    const hasChildren = item.children && item.children.length > 0
    const isSelected = item.path === location.pathname

    // Check if super admin
    const isSuperAdmin = user?.is_superuser || user?.role === 'super_admin' || user?.role === 'Super Administrator'

    // Super admin sees everything
    if (isSuperAdmin) {
      return (
        <React.Fragment key={item.text}>
          <ListItem disablePadding sx={{ pl: sidebarCollapsed ? 0 : depth * 1.5 }}>
            <Tooltip title={sidebarCollapsed ? item.text : ''} placement="right" arrow>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  if (item.path) {
                    if (item.external) {
                      window.open(item.path, '_blank', 'noopener,noreferrer')
                    } else {
                      navigate(item.path)
                      handleSidebarClose()
                    }
                  }
                  if (hasChildren && !sidebarCollapsed) {
                    handleExpandClick(item.text)
                  }
                }}
                sx={{
                  minHeight: depth > 1 ? 40 : 48,
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  px: sidebarCollapsed ? 1.5 : 2,
                }}
              >
                <ListItemIcon sx={{
                  minWidth: sidebarCollapsed ? 0 : (depth > 1 ? 32 : 40),
                  mr: sidebarCollapsed ? 0 : 1,
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="inherit"
                            sx={{
                              fontSize: depth > 1 ? '0.8125rem' : depth > 0 ? '0.875rem' : '1rem',
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          >
                            {item.text}
                          </Typography>
                          {item.external && (
                            <OpenInNewIcon sx={{ fontSize: '0.875rem', opacity: 0.6 }} />
                          )}
                        </Box>
                      }
                    />
                    {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          {hasChildren && !sidebarCollapsed && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children?.map(child => renderMenuItem(child, depth + 1))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      )
    }

    // For non-super admin, check permissions
    const itemHasPermission = item.permission
      ? hasPermission(item.permission)
      : true

    const itemHasRequiredPermissions = item.requiredPermissions
      ? hasAnyPermission(item.requiredPermissions)
      : true

    // Check if any children have permissions (for parent items)
    const hasChildrenWithPermissions = hasChildren
      ? item.children?.some(child => {
        const childHasPermission = child.permission
          ? hasPermission(child.permission)
          : true
        const childHasRequiredPermissions = child.requiredPermissions
          ? hasAnyPermission(child.requiredPermissions)
          : true
        return childHasPermission && childHasRequiredPermissions
      })
      : false

    // Don't render if no permissions and no children with permissions
    if (!itemHasPermission || !itemHasRequiredPermissions) {
      if (!hasChildren || !hasChildrenWithPermissions) {
        return null
      }
    }

    // Show limited access indicator
    const hasLimitedAccess = hasChildren && hasChildrenWithPermissions && (!itemHasPermission || !itemHasRequiredPermissions)

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ pl: sidebarCollapsed ? 0 : depth * 1.5 }}>
          <Tooltip title={sidebarCollapsed ? item.text : ''} placement="right" arrow>
            <ListItemButton
              selected={isSelected}
              onClick={() => {
                if (item.path) {
                  if (item.external) {
                    window.open(item.path, '_blank', 'noopener,noreferrer')
                  } else {
                    navigate(item.path)
                    handleSidebarClose()
                  }
                }
                if (hasChildren && !sidebarCollapsed) {
                  handleExpandClick(item.text)
                }
              }}
              sx={{
                minHeight: depth > 1 ? 40 : 48,
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
                px: sidebarCollapsed ? 1.5 : 2,
              }}
            >
              <ListItemIcon sx={{
                minWidth: sidebarCollapsed ? 0 : (depth > 1 ? 32 : 40),
                mr: sidebarCollapsed ? 0 : 1,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && (
                <>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="inherit"
                          sx={{
                            fontSize: depth > 1 ? '0.8125rem' : depth > 0 ? '0.875rem' : '1rem',
                            fontWeight: isSelected ? 600 : 400,
                            opacity: hasLimitedAccess ? 0.7 : 1,
                          }}
                        >
                          {item.text}
                        </Typography>
                        {item.external && (
                          <OpenInNewIcon sx={{ fontSize: '0.875rem', opacity: 0.6 }} />
                        )}
                        {hasLimitedAccess && (
                          <Tooltip title="Limited access - some features may not be available">
                            <WarningIcon
                              sx={{
                                fontSize: '0.875rem',
                                color: 'warning.main',
                                opacity: 0.7
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                  {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map(child => renderMenuItem(child, depth + 1)).filter(Boolean)}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    )
  }

  const currentDrawerWidth = sidebarCollapsed ? drawerWidthCollapsed : drawerWidth

  // Format date and time
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const drawer = (
    <Box sx={{ width: currentDrawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Admin Header */}
      <Box sx={{ p: sidebarCollapsed ? 1 : 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: sidebarCollapsed ? 0 : 1,
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start'
        }}>
          <Logo
            variant="color"
            size="header"
            showText={!sidebarCollapsed}
          />
        </Box>
        {!sidebarCollapsed && (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              B2B Admin Panel
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.role}
            </Typography>
          </>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {visibleMenuItems.map(item => renderMenuItem(item)).filter(Boolean)}
        </List>
      </Box>

      {/* Logout */}
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        <List>
          <Tooltip title={sidebarCollapsed ? 'Logout' : ''} placement="right" arrow>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                px: sidebarCollapsed ? 1.5 : 2,
                justifyContent: sidebarCollapsed ? 'center' : 'initial',
              }}
            >
              <ListItemIcon sx={{
                minWidth: sidebarCollapsed ? 0 : 40,
                mr: sidebarCollapsed ? 0 : 1,
                justifyContent: 'center'
              }}>
                <LogoutIcon />
              </ListItemIcon>
              {!sidebarCollapsed && <ListItemText primary="Logout" />}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>

      {/* Footer */}
      {!sidebarCollapsed && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Admin Management System v1.0
          </Typography>
        </Box>
      )}
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Admin Header - Two-Tier Structure */}
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Top Bar - Store Links & Info */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
            color: 'white',
            px: { xs: 1.5, sm: 3 },
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 36,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Left: Store Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 1 }}>
              <StorefrontIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                Our Stores:
              </Typography>
            </Box>

            {/* Meesho Link */}
            <Box
              component="a"
              href="https://www.meesho.com/iqqek?ms=2"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Meesho</Typography>
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>

            {/* Amazon Link */}
            <Box
              component="a"
              href="https://www.amazon.in/s?k=DHANSHIKA&ref=bl_dp_s_web_0"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Amazon</Typography>
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>

            {/* IndiaMART Link */}
            <Box
              component="a"
              href="https://www.indiamart.com/dhanshika-exports-imports/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>IndiaMART</Typography>
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>

            {/* Flipkart Link */}
            <Box
              component="a"
              href="https://www.flipkart.com/search?q=DHANSHIKA"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Flipkart</Typography>
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>

            {/* Instagram Link */}
            <Box
              component="a"
              href="https://www.instagram.com/dhanshikaexports/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <InstagramIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Instagram</Typography>
              <OpenInNewIcon sx={{ fontSize: 12 }} />
            </Box>
          </Box>

          {/* Right: Date, Time & Role Selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {formatDate(currentTime)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {formatTime(currentTime)}
                </Typography>
              </Box>
            </Box>

            {/* Integrated Role Selector - Highlighted Display & Simulator */}
            {actualUser?.is_superuser && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  padding: '4px 14px',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <SecurityIcon sx={{ fontSize: 18, color: '#FFD700' }} />
                <FormControl size="small" variant="standard" sx={{ minWidth: 160 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: '0.6rem',
                        fontWeight: 900,
                        letterSpacing: 0.8,
                        textTransform: 'uppercase',
                        lineHeight: 1
                      }}
                    >
                      {user?.role === actualUser.role ? 'Authorized as' : 'Simulating'}
                    </Typography>
                    <Select
                      labelId="role-simulator-label"
                      value={Object.keys(SIMULATED_ROLES).find(k => SIMULATED_ROLES[k as SimulatedRoleKey].name === user?.role) || ''}
                      onChange={(e) => {
                        const roleKey = e.target.value as SimulatedRoleKey
                        const roleData = SIMULATED_ROLES[roleKey]
                        if (user) {
                          setUser({
                            ...user,
                            role: roleData.name,
                            is_superuser: roleData.is_superuser,
                            permissions: roleData.permissions as any
                          })
                        }
                      }}
                      sx={{
                        color: 'white',
                        fontSize: '0.85rem',
                        fontWeight: 900,
                        '&:before': { display: 'none' },
                        '&:after': { display: 'none' },
                        '& .MuiSelect-icon': { color: 'white' },
                        '& .MuiSelect-select': { py: 0, '&:focus': { backgroundColor: 'transparent' } }
                      }}
                    >
                      {Object.entries(SIMULATED_ROLES).map(([key, role]) => (
                        <MuiMenuItem key={key} value={key} sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {role.name}
                            {role.is_superuser && (
                              <Chip
                                label="ROOT"
                                size="small"
                                color="error"
                                sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800 }}
                              />
                            )}
                          </Box>
                        </MuiMenuItem>
                      ))}
                      <MuiMenuItem
                        value="reset"
                        onClick={() => actualUser && setUser(actualUser)}
                        sx={{
                          fontSize: '0.85rem',
                          color: 'error.main',
                          fontWeight: 800,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          mt: 1,
                          backgroundColor: 'rgba(244, 67, 54, 0.08)'
                        }}
                      >
                        EXIT SIMULATION / RESET
                      </MuiMenuItem>
                    </Select>
                  </Box>
                </FormControl>
                {user?.role !== actualUser.role && (
                  <Chip
                    label="LIVE"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 900,
                      backgroundColor: '#FFD700',
                      color: '#000',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Main Toolbar - Logo & Actions */}
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 3 },
            backgroundColor: 'white',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {/* Mobile Menu Toggle */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1.5,
              display: { sm: 'none' },
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop sidebar toggle */}
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              mr: 2.5,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s',
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>

          {/* Logo and Brand */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mr: { xs: 1, sm: 3 },
            }}
          >
            {/* Logo - Clean without borders */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Dhanshika Exports"
                sx={{
                  height: { xs: 40, sm: 48 },
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Quick Action Links - Right Aligned with Permission Gating */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, mr: 1.5 }}>
            {hasPermission('orders.create') && (
              <IconButton
                onClick={() => navigate('/admin/documents/sales-orders/create')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Create Sales Order" arrow>
                  <AddShoppingCartIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}

            {hasPermission('orders.create') && (
              <IconButton
                onClick={() => navigate('/admin/purchase/orders/create')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Create Purchase Order" arrow>
                  <ShoppingBagIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}

            {hasPermission('payments.process') && (
              <IconButton
                onClick={() => navigate('/admin/payments/create')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Take Payment" arrow>
                  <PaymentIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}

            {hasPermission('customers.read') && (
              <IconButton
                onClick={() => navigate('/admin/parties/ledgers')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Party Ledgers" arrow>
                  <AccountBalanceIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}

            {hasPermission('reports.customers') && (
              <IconButton
                onClick={() => navigate('/admin/analytics/customers')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Customer Analytics" arrow>
                  <TrendingUpIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}

            {hasPermission('admin.system.health') && (
              <IconButton
                onClick={() => navigate('/admin/system/health')}
                size="small"
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Tooltip title="Settings" arrow>
                  <SettingsIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            )}
          </Box>

          {/* Quick Actions - Existing component */}
          <Box sx={{ mr: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'block' } }}>
            <QuickActions
              permissions={user?.permissions || {}}
              onAction={handleQuickAction}
            />
          </Box>

          {/* User Profile Dropdown */}
          <UserProfileDropdown
            user={{
              id: user?.id || '1',
              email: user?.email || '',
              role: user?.role || 'User',
              name: (user as any)?.name || (user as any)?.full_name || 'Admin User',
              permissions: user?.permissions || {},
              is_superuser: user?.is_superuser
            }}
            onLogout={handleLogout}
          />
        </Toolbar>
      </AppBar>

      {/* Admin Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleSidebarClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: '92px', sm: '100px' }, // Height of two-tier AppBar (top bar + toolbar)
          pl: !isMobile && sidebarOpen ? 0 : 0,
          transition: theme.transitions.create(['margin', 'padding'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Breadcrumb Navigation */}
          <Breadcrumbs />
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout
